import type { useTranslations } from "use-intl";
import {
  string,
  object,
  ref,
  date,
  number,
  array,
  lazy,
  ObjectSchema,
  mixed,
} from "yup";
import { TravelPurpose } from "@/app/lib/types/enums";
import type { THotelFormValues } from "@/app/lib/types/types";
import { MIN_ADULT_AGE } from "@/app/lib/constants";
import { citySchema } from "@/app/lib/utils/locations";

export const convertArabicNumberToEnglish = (n: string) => {
  const ar = "٠١٢٣٤٥٦٧٨٩".split("");
  const en = "0123456789".split("");

  const englishNumberStr = n
    .replace(/[٠١٢٣٤٥٦٧٨٩]/g, (x) => en[ar.indexOf(x)])
    .replace(/[^\d]/g, "");

  return Number(englishNumberStr);
};

export const getDatesValidationSchema = (
  t: ReturnType<typeof useTranslations>,
) =>
  object().shape({
    checkIn: date().required(t("hotels.validation.dates.checkIn")),
    checkOut: date()
      .min(ref("checkIn"), t("hotels.validation.dates.min"))
      .required(t("hotels.validation.dates.checkOut")),
  });

export const getRoomsValidationSchema = (
  t: ReturnType<typeof useTranslations>,
) =>
  array()
    .of(
      object().shape({
        adults: number().integer().min(1),
        children: number().integer(),
        childrenAges: array().of(
          lazy((value) =>
            (isNaN(Number(value))
              ? string().transform(convertArabicNumberToEnglish)
              : number().integer()
            )
              .min(0, t("hotels.validation.childrenAge"))
              .max(MIN_ADULT_AGE - 1, t("hotels.validation.childrenAge"))
              .required(),
          ),
        ),
      }),
    )
    .min(1, t("hotels.validation.roomsMin"))
    .max(5, t("hotels.validation.roomsMax"))
    .required();

export const getValidationSchema = (t: ReturnType<typeof useTranslations>) =>
  object().shape({
    destination: object()
      .shape({
        id: number().required(),
        name: string().required(),
      })
      .required(t("hotels.validation.destination")),
    dates: getDatesValidationSchema(t),
    rooms: getRoomsValidationSchema(t),
    travelPurpose: mixed<TravelPurpose>()
      .oneOf(Object.values(TravelPurpose))
      .required(),
  });

export const hotelFormValuesSchema: ObjectSchema<THotelFormValues> = object({
  destination: citySchema.required(),
  dates: object({
    checkIn: date().required(),
    checkOut: date().required(),
  }).required(),
  rooms: array()
    .of(
      object({
        adults: number().required().min(1),
        children: number().required().min(0),
        childrenAges: array()
          .of(
            lazy((value) =>
              (isNaN(Number(value))
                ? string().transform(convertArabicNumberToEnglish)
                : number().integer()
              )
                .min(0)
                .max(MIN_ADULT_AGE - 1)
                .required(),
            ),
          )
          .required(),
      }),
    )
    .required(),
  travelPurpose: mixed<TravelPurpose>()
    .oneOf(Object.values(TravelPurpose))
    .required(),
});
